import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from 'src/layout';
import Container from '@/components/ui/container';
import InputUi from '@/components/ui/inputui';
import RichTextUi from '@/components/ui/RichTextUi';
import UploadImages from '@/components/ui/uploadImages';
import SelectProducts from '@/components/ui/selectProducts';
import OfferProducts from '@/components/products/offersProducts';
import Pricing from '@/components/products/pricing';
import CategoryProduct from '@/components/products/categoryProduct';
import CrossSellModal from '@/components/products/crossSellModal';
import axiosInstance from 'src/lib/axiosInstance';
import { toast } from 'react-toastify';

const EditMakeCombo = () => {
    const { comboID } = useParams();
    const uploadRef = useRef();
    const galleryRef = useRef();

    const [loading, setLoading] = useState(true);
    const [selectedProductIDs, setSelectedProductIDs] = useState([]);
    const [showCrossSellModal, setShowCrossSellModal] = useState(false);
    const [crossSells, setCrossSells] = useState([]);

    const [product, setProduct] = useState({
        name: '',
        description: '',
        tab1: '',
        tab2: '',
        tab3: '',
        type: 'variable',
        featuredImage: [],
        galleryImage: [],
        categories: [],
        products: [],

    });

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const res = await axiosInstance.get(`/make-combo/detail/${comboID}`);
                const data = res.data;

                const parsedProduct = {
                    ...data,
                    name: data.name ?? '',
                    description: data.description ?? '',
                    tab1: data.tab1 ?? '',
                    tab2: data.tab2 ?? '',
                    tab3: data.tab3 ?? '',
                    featuredImage: parseJSONSafe(data.featuredImage) ?? [],
                    galleryImage: parseJSONSafe(data.galleryImage) ?? [],
                    productAttributes: parseJSONSafe(data.productAttributes) ?? [],
                    variations: Array.isArray(data.variations)
                        ? data.variations.map(variation => ({
                            ...variation,
                            variationValues: parseJSONSafe(variation.variationValues),
                        }))
                        : [],
                    categories: parseJSONSafe(data.categories) ?? [],
                    products: parseJSONSafe(data.products) ?? [],
                };

                setProduct(parsedProduct);
                setSelectedProductIDs(parsedProduct.products);

                // Extract cross-sell product IDs
                if (data.crossSellProducts && Array.isArray(data.crossSellProducts)) {
                    setCrossSells(data.crossSellProducts.map(p => p.productID || p.crossSellProductID));
                }

                console.log(parsedProduct);

            } catch (error) {
                console.error('Error fetching product details:', error);
                toast.error('Failed to load combo details');
            } finally {
                setLoading(false);
            }
        };

        if (comboID) fetchProduct();
    }, [comboID]);

    const parseJSONSafe = (value) => {
        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch {
                return value;
            }
        }
        return value;
    };

    const updateFunction = (e, name) => {
        setProduct(prev => ({
            ...prev,
            [name]: e.target.value
        }));
    };

    const handleToggleProductParent = (productID) => {
        setSelectedProductIDs(prev =>
            prev.includes(productID)
                ? prev.filter(id => id !== productID)
                : [...prev, productID]
        );
    };

    const handleUpload = async () => {
        if (selectedProductIDs.length === 0) {
            toast.error('Please select at least one product for the combo pool.');
            return;
        }
        const reqSel = Number(product.requiredSelections);
        if (!product.requiredSelections || isNaN(reqSel) || reqSel <= 0) {
            toast.error('Please enter how many items the user needs to select to make this combo.');
            return;
        }
        if (reqSel > selectedProductIDs.length) {
            toast.error(`Required selection (${reqSel}) cannot be greater than available products in pool (${selectedProductIDs.length}).`);
            return;
        }

        try {
            const [featuredImage, galleryImage] = await Promise.all([
                uploadRef.current?.uploadImageFunction(),
                galleryRef.current?.uploadImageFunction(),
            ]);

            const fullProductData = {
                ...product,
                featuredImage,
                galleryImage,
                products: selectedProductIDs,
                crossSells: crossSells,
                comboID, // include comboID for editing
            };

            const response = await axiosInstance.put(`/make-combo/edit/${comboID}`, fullProductData);
            const result = response.data;
            if (response.status !== 200) throw new Error(result.message || 'Failed to edit product');
            toast.success(result?.message || 'Combo updated successfully!');
        } catch (error) {
            console.error('Error uploading or posting product:', error.response?.data || error.message);
            toast.error(error.response?.data?.message || error.message || 'Failed to update combo product');
        }
    };

    if (loading) return <div className="p-5 text-lg">Loading combo...</div>;

    return (
        <Layout active={'admin-mcombo-list'} title={'Edit Make Combo'}>
            <div className="grid grid-cols-6 gap-2">
                <div className="col-span-4 flex flex-col gap-2">
                    <Container gap={3} label={'Basic Information'}>
                        <InputUi
                            label={'Product Title'}
                            value={product.name ?? ''}
                            datafunction={(e) => updateFunction(e, 'name')}
                        />
                        <InputUi
                            label={'Required Products User Must Select to Make Combo (e.g. 3)'}
                            type="number"
                            min="1"
                            value={product.requiredSelections ?? ''}
                            datafunction={(e) => updateFunction(e, 'requiredSelections')}
                        />
                        <RichTextUi
                            label={'Product Description'}
                            value={product.description ?? ''}
                            onChange={(val) => updateFunction({ target: { value: val } }, 'description')}
                        />
                        <div className="grid grid-cols-3 gap-2">
                            <RichTextUi
                                label={'Tab 1'}
                                value={product.tab1 ?? ''}
                                onChange={(val) => updateFunction({ target: { value: val } }, 'tab1')}
                            />
                            <RichTextUi
                                label={'Tab 2'}
                                value={product.tab2 ?? ''}
                                onChange={(val) => updateFunction({ target: { value: val } }, 'tab2')}
                            />
                            <RichTextUi
                                label={'Tab 3'}
                                value={product.tab3 ?? ''}
                                onChange={(val) => updateFunction({ target: { value: val } }, 'tab3')}
                            />
                        </div>
                    </Container>

                    <Container gap={3} label={'Pricing & Discount'}>
                        <Pricing setProducts={setProduct} products={product} />
                    </Container>

                    <Container gap={3} label={'Select Pool of Products Available for this Combo'}>
                        <SelectProducts
                            initialSelected={selectedProductIDs}
                            onProductToggle={handleToggleProductParent}
                        />
                    </Container>

                    <Container gap={3} label={'Cross-Sell Products'}>
                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                onClick={() => setShowCrossSellModal(true)}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                Select Cross-Sell Products
                            </button>
                            {crossSells.length > 0 && (
                                <div className="text-sm text-secondary-text">
                                    {crossSells.length} product{crossSells.length !== 1 ? 's' : ''} selected
                                </div>
                            )}
                        </div>
                    </Container>
                </div>

                <div className="col-span-2 flex flex-col gap-2">
                    {/* <Container containerclass={'bg-dark-text'}>
                        <div className="overflow-x-auto">
                            <pre className="col-span-2 mt-4 p-2 text-white rounded text-xs whitespace-pre max-w-full">
                                {JSON.stringify(product, null, 3)}
                            </pre>
                        </div>
                    </Container> */}

                    <Container label={'Categories'}>
                        <CategoryProduct
                            setProducts={setProduct}
                            products={product}
                            isEditable={true}
                            oldValue={product.categories}
                        />
                    </Container>

                    <Container gap={3} label={'Offers & Promotions'}>
                        <OfferProducts setProducts={setProduct} products={product} />
                    </Container>

                    <Container gap={3} label={'Featured Images'}>
                        <UploadImages ref={uploadRef} maxImages={2} defaultImages={product.featuredImage} />
                    </Container>

                    <Container gap={3} label={'Gallery Images'}>
                        <UploadImages ref={galleryRef} maxImages={8} defaultImages={product.galleryImage ?? []} />
                    </Container>

                    <button className="primary-button" onClick={handleUpload}>
                        Update Product
                    </button>
                </div>
            </div>
            <CrossSellModal
                isOpen={showCrossSellModal}
                onClose={() => setShowCrossSellModal(false)}
                onSave={(selected) => setCrossSells(selected)}
                initialSelected={crossSells}
            />
        </Layout>
    );
};

export default EditMakeCombo;
